import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Heart } from 'lucide-react';

const authFormSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
});

type AuthFormValues = z.infer<typeof authFormSchema>;

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const form = useForm<AuthFormValues>({
    resolver: zodResolver(authFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: AuthFormValues) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      navigate('/dashboard');
    } catch (error) {
      console.error('Authentication error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-indigo-950">
      {/* Left column - App description */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-8 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="max-w-md mx-auto space-y-8">
          <div className="flex items-center justify-center">
            <Heart className="h-12 w-12 text-white fill-white mr-2" />
            <h1 className="text-4xl font-bold">ADA</h1>
          </div>
          
          <h2 className="text-2xl font-semibold">Abuse Detection Application</h2>
          
          <p className="text-lg opacity-90">
            A game-based system designed to help detect manifestations of abuse in children 
            through their responses while playing.
          </p>
          
          <div className="space-y-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <h3 className="font-medium mb-2">Game Journey</h3>
              <p>Children embark on an adventure through five themed stages where they collect value badges.</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <h3 className="font-medium mb-2">Value Badges</h3>
              <p>Love, Hard Work, Honesty, Courage, and Hope - earning an adventure-seeker trophy.</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <h3 className="font-medium mb-2">Secure Data Analysis</h3>
              <p>Protected analytics and insights to help identify potential abuse manifestations.</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Right column - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-4 py-12 sm:px-6">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          <div className="text-center">
            <div className="flex items-center justify-center lg:hidden">
              <Heart className="h-8 w-8 text-blue-600 fill-blue-600 mr-2" />
              <h1 className="text-3xl font-bold tracking-tight text-foreground">ADA</h1>
            </div>
            <h2 className="mt-2 text-xl font-semibold tracking-tight text-foreground lg:hidden">
              Abuse Detection Application
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Admin Panel - Secure access to the system
            </p>
          </div>

          <Card className="glass-morphism">
            <CardHeader>
              <CardTitle className="text-center">Sign In</CardTitle>
              <CardDescription className="text-center">
                Enter your credentials to access your account
              </CardDescription>
            </CardHeader>

            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input placeholder="your@email.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="••••••••" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
          
          <div className="text-center text-xs text-muted-foreground mt-4">
            <p>Developed by Bidlisiw Foundation, Inc.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
